import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { AdminConsultationService } from '../../../../shared/services/admin-consultation.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { SlotActionsModalComponent, SlotAction } from '../../../../modals/admin/slot-actions-modal/slot-actions-modal.component';
import { IAdminSlot, IBulkCreateSlotsRequest } from '../../../../../assets/types/dtoTypes';

interface WeekdayOption { value: string; label: string; selected: boolean; }
interface DayColumn { date: Date; label: string; dayNum: string; isToday: boolean; slots: IAdminSlot[]; }

@Component({
  selector: 'app-slot-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './slot-admin-page.component.html',
  styleUrls: ['./slot-admin-page.component.scss'],
})
export class SlotAdminPageComponent implements OnInit {
  private readonly adminService = inject(AdminConsultationService);
  private readonly popup = inject(ShowPopupService);
  private readonly dialog = inject(MatDialog);

  // ----- bulk generation form --------------------------------------------
  weekdays: WeekdayOption[] = [
    { value: 'MONDAY', label: 'Mon', selected: true },
    { value: 'TUESDAY', label: 'Tue', selected: true },
    { value: 'WEDNESDAY', label: 'Wed', selected: true },
    { value: 'THURSDAY', label: 'Thu', selected: true },
    { value: 'FRIDAY', label: 'Fri', selected: true },
    { value: 'SATURDAY', label: 'Sat', selected: false },
    { value: 'SUNDAY', label: 'Sun', selected: false },
  ];
  form = {
    fromDate: this.isoDate(new Date()),
    toDate: this.isoDate(this.addDays(new Date(), 14)),
    dailyStart: '09:00',
    dailyEnd: '17:00',
    slotDurationMinutes: 45,
    breakMinutes: 15,
    capacity: 1,
  };
  generating = signal<boolean>(false);

  // ----- week calendar ----------------------------------------------------
  weekStart = signal<Date>(this.startOfWeek(new Date()));
  slots = signal<IAdminSlot[]>([]);
  isLoading = signal<boolean>(true);
  busyId = signal<string | null>(null);

  weekLabel = computed(() => {
    const start = this.weekStart();
    const end = this.addDays(start, 6);
    const sameMonth = start.getMonth() === end.getMonth();
    const startStr = start.toLocaleDateString('en-GB', { day: 'numeric', month: sameMonth ? undefined : 'short' });
    const endStr = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startStr} – ${endStr}`;
  });

  days = computed<DayColumn[]>(() => {
    const start = this.weekStart();
    const all = this.slots();
    const todayKey = this.dayKey(new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const date = this.addDays(start, i);
      const key = this.dayKey(date);
      return {
        date,
        label: date.toLocaleDateString('en-GB', { weekday: 'short' }),
        dayNum: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        isToday: key === todayKey,
        slots: all
          .filter(s => this.dayKey(new Date(s.startAt)) === key)
          .sort((a, b) => a.startAt.localeCompare(b.startAt)),
      };
    });
  });

  ngOnInit(): void {
    this.loadWeek();
  }

  // ----- bulk generation --------------------------------------------------

  get selectedDays(): string[] {
    return this.weekdays.filter(d => d.selected).map(d => d.value);
  }

  get canGenerate(): boolean {
    return this.selectedDays.length > 0
        && !!this.form.fromDate && !!this.form.toDate
        && this.form.dailyEnd > this.form.dailyStart
        && this.form.slotDurationMinutes > 0;
  }

  generate(): void {
    if (!this.canGenerate) return;
    const body: IBulkCreateSlotsRequest = {
      fromDate: this.form.fromDate,
      toDate: this.form.toDate,
      daysOfWeek: this.selectedDays,
      dailyStart: this.form.dailyStart,
      dailyEnd: this.form.dailyEnd,
      slotDurationMinutes: this.form.slotDurationMinutes,
      breakMinutes: this.form.breakMinutes,
      capacity: this.form.capacity,
    };
    this.generating.set(true);
    this.adminService.bulkCreateSlots(body)
      .pipe(finalize(() => this.generating.set(false)))
      .subscribe({
        next: (res) => {
          this.popup.showSuccessPopup({
            title: 'Slots generated',
            message: `${res.created} created, ${res.skipped} skipped (past or overlapping).`,
          });
          // Jump the calendar to the first generated week so the result is visible.
          if (res.slots.length > 0) {
            this.weekStart.set(this.startOfWeek(new Date(res.slots[0].startAt)));
          }
          this.loadWeek();
        },
        error: (err) => this.fail(err?.error?.message || 'Could not generate slots'),
      });
  }

  // ----- week navigation --------------------------------------------------

  prevWeek(): void { this.weekStart.set(this.addDays(this.weekStart(), -7)); this.loadWeek(); }
  nextWeek(): void { this.weekStart.set(this.addDays(this.weekStart(), 7)); this.loadWeek(); }
  today(): void { this.weekStart.set(this.startOfWeek(new Date())); this.loadWeek(); }

  loadWeek(): void {
    this.isLoading.set(true);
    const from = new Date(this.weekStart());
    const to = this.addDays(from, 6);
    to.setHours(23, 59, 59, 999);
    this.adminService.listSlots(from, to)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (rows) => this.slots.set(rows),
        error: () => this.fail('Could not load slots'),
      });
  }

  // ----- slot actions -----------------------------------------------------

  openSlot(slot: IAdminSlot): void {
    this.dialog.open(SlotActionsModalComponent, { data: { slot }, panelClass: 'admin-action-panel' })
      .afterClosed().subscribe((action: SlotAction | undefined) => {
        if (action === 'toggle') this.toggleActive(slot);
        else if (action === 'delete') this.deleteSlot(slot);
      });
  }

  slotClass(slot: IAdminSlot): string {
    if (!slot.active) return 'off';
    if (slot.bookedCount > 0) return 'booked';
    return slot.available ? 'free' : 'past';
  }

  private toggleActive(slot: IAdminSlot): void {
    this.busyId.set(slot.id);
    this.adminService.updateSlot(slot.id, { active: !slot.active })
      .pipe(finalize(() => this.busyId.set(null)))
      .subscribe({
        next: () => { this.popup.showSuccessPopup({ title: slot.active ? 'Slot deactivated' : 'Slot activated' }); this.loadWeek(); },
        error: (err) => this.fail(err?.error?.message || 'Update failed'),
      });
  }

  private deleteSlot(slot: IAdminSlot): void {
    this.busyId.set(slot.id);
    this.adminService.deleteSlot(slot.id)
      .pipe(finalize(() => this.busyId.set(null)))
      .subscribe({
        next: () => { this.popup.showSuccessPopup({ title: 'Slot deleted' }); this.loadWeek(); },
        error: (err) => this.fail(err?.error?.message || 'Could not delete slot'),
      });
  }

  // ----- helpers ----------------------------------------------------------

  private isoDate(d: Date): string { return d.toISOString().slice(0, 10); }
  private addDays(d: Date, days: number): Date { const r = new Date(d); r.setDate(r.getDate() + days); return r; }
  private dayKey(d: Date): string {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }
  private startOfWeek(d: Date): Date {
    const r = new Date(d);
    const day = (r.getDay() + 6) % 7; // 0 = Monday
    r.setDate(r.getDate() - day);
    r.setHours(0, 0, 0, 0);
    return r;
  }
  private fail(message: string): void {
    this.popup.showFailurePopup({ title: 'Error', message });
  }
}
